import type { ReactNode } from 'react';
import type { ChatSearchResult } from '@volt/shared';
import { Icon } from './Icon.js';
import { SwipeRow } from './SwipeRow.js';
import { formatListTime, type ChatListEntry } from './directChatList.js';

type Translate = (key: never, params?: Record<string, string | number>) => string;

interface ChatListViewProps {
  t: Translate;
  locale: string;
  entries: ChatListEntry[];
  strip: ChatListEntry[];
  archivedCount: number;
  showArchived: boolean;
  search: string;
  searchResults: ChatSearchResult[] | null;
  searching: boolean;
  entryTitle: (result: ChatSearchResult) => string;
  loading: boolean;
  drafts: Record<string, string>;
  onSearch: (value: string) => void;
  onOpen: (key: string) => void;
  onActions: (key: string) => void;
  onSwipePin: (key: string) => void;
  onSwipeArchive: (key: string) => void;
  onSwipeDelete: (key: string) => void;
  onRowPointerDown: (key: string, event: { clientX: number; clientY: number }) => void;
  onRowPointerMove: (event: { clientX: number; clientY: number }) => void;
  onRowPointerEnd: () => void;
  onToggleArchived: () => void;
  onAdd: () => void;
  onOpenResult: (result: ChatSearchResult) => void;
}

function Avatar({ entry, className }: { entry: ChatListEntry; className: string }) {
  return entry.photoUrl ? (
    <img className={`direct-chat-avatar ${className}`} src={entry.photoUrl} alt="" />
  ) : (
    <span
      className={`direct-chat-avatar direct-chat-avatar-fallback ${className}`}
      aria-hidden="true"
    >
      {entry.initials}
    </span>
  );
}

// Plain function (not a component) so the parent owns all hook state.
export function renderChatList(props: ChatListViewProps): ReactNode {
  const t = props.t as (key: string, params?: Record<string, string | number>) => string;
  const now = new Date();
  const searching = props.search.trim().length >= 2;
  return (
    <div className="direct-chat-list">
      {props.strip.length > 0 && (
        <div
          className="direct-chat-strip"
          role="group"
          aria-label={t('directChat.friends')}
          data-no-swipe
        >
          {props.strip.map((friend) => (
            <button
              type="button"
              key={`strip:${friend.key}`}
              className="direct-chat-strip-item"
              data-chat-key={friend.key}
              onClick={() => props.onOpen(friend.key)}
            >
              <Avatar entry={friend} className="direct-chat-strip-avatar" />
              <span>{friend.title}</span>
            </button>
          ))}
        </div>
      )}
      <div className="direct-chat-list-toolbar">
        <label className="direct-chat-search">
          <Icon name="search" size={16} />
          <input
            type="search"
            value={props.search}
            onChange={(event) => props.onSearch(event.target.value)}
            placeholder={t('directChat.searchPlaceholder')}
            aria-label={t('directChat.searchPlaceholder')}
            autoComplete="off"
            maxLength={100}
          />
        </label>
        <button
          type="button"
          className="icon-btn direct-chat-add-button"
          onClick={props.onAdd}
          aria-label={t('directChat.addMenu')}
        >
          <Icon name="plus" size={22} />
        </button>
      </div>
      {searching ? (
        <div className="direct-chat-rows" role="list" aria-label={t('directChat.searchResults')}>
          {props.searching && !props.searchResults ? (
            <p className="direct-chat-empty" role="status">
              {t('common.loading')}
            </p>
          ) : props.searchResults?.length ? (
            props.searchResults.map((result) => (
              <button
                type="button"
                role="listitem"
                key={`${result.kind}:${result.targetId}:${result.message.id}`}
                className="direct-chat-row"
                onClick={() => props.onOpenResult(result)}
              >
                <span className="direct-chat-row-body">
                  <span className="direct-chat-row-head">
                    <strong>{props.entryTitle(result)}</strong>
                    <time dateTime={result.message.createdAt}>
                      {formatListTime(result.message.createdAt, now, props.locale)}
                    </time>
                  </span>
                  <span className="direct-chat-row-preview">{result.message.content}</span>
                </span>
              </button>
            ))
          ) : (
            <p className="direct-chat-empty">{t('directChat.noResults')}</p>
          )}
        </div>
      ) : (
        <div className="direct-chat-rows" role="list" aria-label={t('directChat.chats')}>
          {props.archivedCount > 0 || props.showArchived ? (
            <button
              type="button"
              className="direct-chat-archive-row"
              onClick={props.onToggleArchived}
            >
              <Icon name={props.showArchived ? 'back' : 'archive'} size={18} />
              {props.showArchived
                ? t('directChat.backToChats')
                : t('directChat.archivedChats', { count: props.archivedCount })}
            </button>
          ) : null}
          {props.entries.map((entry) => (
            <div
              role="listitem"
              key={entry.key}
              className={`direct-chat-row-wrap${entry.pinnedAt ? ' is-pinned' : ''}${entry.muted ? ' is-muted' : ''}`}
            >
              <SwipeRow
                className="direct-chat-swipe-wrap"
                actions={[
                  {
                    id: 'pin',
                    label: entry.pinnedAt ? t('directChat.unpin') : t('directChat.pin'),
                    icon: 'pin',
                    onClick: () => props.onSwipePin(entry.key),
                  },
                  {
                    id: 'archive',
                    label: entry.archived ? t('directChat.unarchive') : t('directChat.archive'),
                    icon: 'archive',
                    onClick: () => props.onSwipeArchive(entry.key),
                  },
                  {
                    id: 'delete',
                    label: t('directChat.delete'),
                    icon: 'trash',
                    tone: 'danger',
                    onClick: () => props.onSwipeDelete(entry.key),
                  },
                ]}
              >
                <button
                  type="button"
                  className="direct-chat-row"
                  data-chat-key={entry.key}
                  onClick={() => props.onOpen(entry.key)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    props.onActions(entry.key);
                  }}
                  onPointerDown={(event) => props.onRowPointerDown(entry.key, event)}
                  onPointerUp={props.onRowPointerEnd}
                  onPointerMove={props.onRowPointerMove}
                  onPointerCancel={props.onRowPointerEnd}
                >
                  <Avatar entry={entry} className="direct-chat-row-avatar" />
                  <span className="direct-chat-row-body">
                    <span className="direct-chat-row-head">
                      <strong>{entry.title}</strong>
                      {entry.muted && (
                        <span className="direct-chat-muted-mark" aria-label={t('directChat.muted')}>
                          🔕
                        </span>
                      )}
                      {entry.pinnedAt && <Icon name="pin" size={12} />}
                      {entry.lastAt && (
                        <time dateTime={entry.lastAt}>
                          {formatListTime(entry.lastAt, now, props.locale)}
                        </time>
                      )}
                    </span>
                    <span className="direct-chat-row-foot">
                      <span className="direct-chat-row-preview">
                        {props.drafts[entry.key]?.trim() ? (
                          <>
                            <span className="direct-chat-draft">{t('directChat.draft')}</span>{' '}
                            {props.drafts[entry.key]!.trim()}
                          </>
                        ) : entry.blocked ? (
                          t('directChat.blockedBadge')
                        ) : entry.previewMine ? (
                          `${t('common.you')}: ${entry.preview}`
                        ) : (
                          entry.preview ||
                          (entry.kind === 'group' ? t('directChat.noMessages') : '')
                        )}
                      </span>
                      {entry.receipt && (
                        <span
                          className={`direct-chat-ticks${entry.receipt === '✓✓' ? ' is-read' : ''}`}
                          aria-label={
                            entry.receipt === '✓✓'
                              ? t('directChat.read')
                              : t('directChat.delivered')
                          }
                        >
                          {entry.receipt}
                        </span>
                      )}
                      {entry.unread > 0 && (
                        <span
                          className={`direct-chat-unread${entry.muted ? ' is-muted' : ''}`}
                          aria-label={t('directChat.unread', { count: entry.unread })}
                        >
                          {entry.unread > 99 ? '99+' : entry.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="icon-btn direct-chat-row-more"
                  onClick={() => props.onActions(entry.key)}
                  aria-label={`${t('directChat.chatActions')}: ${entry.title}`}
                >
                  <Icon name="more" size={18} />
                </button>
              </SwipeRow>
            </div>
          ))}
          {!props.loading && props.entries.length === 0 && (
            <p className="direct-chat-empty">
              {props.showArchived ? t('directChat.noArchived') : t('directChat.noChats')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
